// Surface 3 — every change in the period, newest first.
//
// The question that brings a seller back after they have acted: nine titles
// were rewritten last week, did any of it work.
import React from 'react';
import s from '../CTROptimizer.module.css';
import { num, pct } from '../format';

export default function Changes({ data }) {
  if (!data) return <div className={s.empty}>Loading…</div>;

  if (!data.available) {
    return (
      <div className={s.empty}>
        <p>No change history is being recorded.</p>
        <p className={s.hint}>
          Changes are found by comparing one day&apos;s snapshot of your listings with the
          next — Amazon publishes no change history, so a change is only knowable by having
          looked before. No snapshot has been taken yet.
        </p>
      </div>
    );
  }

  const a = data.summary;
  return (
    <>
      <div className={s.stats}>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.improved)}</span>
          <span className={s.statLabel}>improved</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.worsened)}</span>
          <span className={s.statLabel}>worsened</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.still_accruing)}</span>
          <span className={s.statLabel}>still accruing</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.not_comparable)}</span>
          <span className={s.statLabel}>not comparable</span>
        </div>
      </div>
      <p className={s.hint} style={{ margin: '4px 0 14px' }}>
        Reported as observed, never proven. Price, competitors, season and demand all move
        inside the same window as a listing change, so what this says is that click rate
        moved after the change — not that the change caused it.
      </p>

      {/* A table of price rows alone reads as "no title or image changed". What
          is true is narrower: those are seen by comparing one snapshot with the
          next, and until a second exists there is nothing to compare. */}
      {data.watching && (
        <div className={s.section} style={{ marginBottom: 14 }}>
          <div className={s.sectionTitle}>What is being watched</div>
          <ul className={s.cardList}>
            {['price', 'title', 'main image'].map((kind) => {
              const w = data.watching[kind];
              if (!w) return null;
              return (
                <li key={kind}>
                  <span className={s.cardTerm}>
                    <span className={w.watched ? s.direct : s.undetermined}>
                      {w.watched ? 'watching' : 'not yet'}
                    </span>{' '}{kind}
                  </span>
                  <span className={s.cardNote} style={{ textAlign: 'right' }}>{w.note}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Date</th><th>Product</th><th>What changed</th>
              <th className={s.numeric}>Before</th><th className={s.numeric}>After</th>
              <th className={s.numeric}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {data.changes.map((c, i) => (
              <tr key={`${c.asin}-${c.date}-${i}`}>
                <td>{c.date}</td>
                <td className={s.asin}>{c.asin}</td>
                <td>{c.what}</td>
                <td className={s.numeric}>{pct(c.before)}</td>
                <td className={s.numeric}>{pct(c.after)}</td>
                <td className={s.numeric}>
                  {c.difference == null
                    ? <span className={s.undetermined}>{c.state}</span>
                    : <span className={c.difference >= 0 ? s.up : s.down}>
                        {c.difference >= 0 ? '+' : ''}{(c.difference * 100).toFixed(2)}pp
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
